import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  InputNumber,
  Radio,
  Descriptions,
  Divider,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useStores } from '../../../stores';
import './Inventory.scss';

const { Title, Text } = Typography;

const InventoryEdit = observer(() => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { partnerInventoryStore } = useStores();
  const { currentInventory, loading, error } = partnerInventoryStore;

  const [form] = Form.useForm();

  useEffect(() => {
    partnerInventoryStore.fetchInventoryById(id);
  }, [partnerInventoryStore, id]);

  useEffect(() => {
    if (currentInventory) {
      form.setFieldsValue({
        price: parseFloat(currentInventory.price),
        shipping_cost: parseFloat(currentInventory.shipping_cost),
        stock_quantity: currentInventory.stock_quantity,
        is_active: currentInventory.is_active,
      });
    }
  }, [currentInventory, form]);

  const handleSubmit = async (values) => {
    const result = await partnerInventoryStore.updateInventory(id, values);

    if (result.success) {
      message.success(t('partner.products.messages.updateSuccess'));
      // Refresh inventory list before navigating
      await partnerInventoryStore.fetchInventory();
      navigate('/partner/inventory');
    } else {
      message.error(result.error || t('partner.products.messages.updateFailed'));
    }
  };

  if (loading && !currentInventory) {
    return (
      <div className="inventory-edit-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!currentInventory) {
    return (
      <div className="inventory-edit">
        <Alert
          message={t('partner.products.productNotFound')}
          description={t('partner.products.productNotFoundDesc')}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="inventory-edit">
      <div className="page-header">
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/partner/inventory')}
          >
            {t('partner.products.backToProducts')}
          </Button>
          <Title level={2}>{t('partner.products.editProduct')}</Title>
        </Space>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card title={t('partner.products.productInformation')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered>
          <Descriptions.Item label={t('partner.products.form.productName')}>
            {currentInventory.product_name}
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.products.sku')}>
            {currentInventory.sku}
          </Descriptions.Item>
          {currentInventory.color && (
            <Descriptions.Item label={t('partner.products.color')}>
              {currentInventory.color}
            </Descriptions.Item>
          )}
          {currentInventory.size && (
            <Descriptions.Item label={t('partner.products.size')}>
              {currentInventory.size}
            </Descriptions.Item>
          )}
          {currentInventory.weight && (
            <Descriptions.Item label={t('partner.products.weight')}>
              {currentInventory.weight}g
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={t('partner.products.updatePricingStock')}>
        <Alert
          message={t('common.note')}
          description={t('partner.products.updatePricingNote')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="price"
            label={t('partner.products.form.price')}
            rules={[
              { required: true, message: t('partner.products.form.priceRequired') },
              { type: 'number', min: 0.01, message: t('partner.products.form.priceMin') },
            ]}
          >
            <InputNumber
              placeholder={t('partner.products.form.pricePlaceholder')}
              style={{ width: '100%' }}
              precision={2}
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item
            name="shipping_cost"
            label={t('partner.products.form.shippingCost')}
            rules={[
              { required: true, message: t('partner.products.form.shippingRequired') },
              { type: 'number', min: 0, message: t('partner.products.form.shippingMin') },
            ]}
          >
            <InputNumber
              placeholder={t('partner.products.form.shippingPlaceholder')}
              style={{ width: '100%' }}
              precision={2}
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item
            name="stock_quantity"
            label={t('partner.products.form.stockQuantity')}
            rules={[
              { required: true, message: t('partner.products.form.stockRequired') },
              { type: 'number', min: 0, message: t('partner.products.form.stockMin') },
            ]}
          >
            <InputNumber
              placeholder={t('partner.products.form.stockPlaceholder')}
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>

          <Form.Item name="is_active" label={t('common.active')}>
            <Radio.Group>
              <Radio value={true}>{t('common.yes')}</Radio>
              <Radio value={false}>{t('common.no')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="large"
            >
              {t('common.saveChanges')}
            </Button>
            <Button
              size="large"
              onClick={() => navigate('/partner/inventory')}
            >
              {t('common.cancel')}
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
});

export default InventoryEdit;
